import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../lib/api.js';
import { grossInr, useCreateInvoice } from '../lib/billing.js';
import { useProject } from '../lib/projects.js';
import { formatInr } from '../lib/format.js';

/* ============================================================================
   /invoices/new?projectId=... — raise an invoice by hand.
   ----------------------------------------------------------------------------
   Owner-only, gated in App.tsx by RequireRole and by the API's
   requireRole('owner'). The gate that matters is the API's.

   There is no number field. The number is generated server-side in the Indian
   financial year's sequence, because a gap or a duplicate in a GST sequence is
   a statutory problem rather than a tidiness one.

   There is no `care` type either: those are raised by the nightly billing job
   from an active plan, and one typed here would bill the client twice.
   ========================================================================== */

export function NewInvoicePage() {
  const [params] = useSearchParams();
  const projectId = params.get('projectId') ?? '';
  const navigate = useNavigate();

  const project = useProject(projectId);
  const create = useCreateInvoice();

  const [type, setType] = useState<'advance' | 'final'>('advance');
  const [amountInr, setAmountInr] = useState('');
  const [gstPercent, setGstPercent] = useState('18');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      {
        projectId,
        type,
        amountInr: Number(amountInr),
        gstPercent: Number(gstPercent),
      },
      { onSuccess: () => void navigate('/invoices', { replace: true }) },
    );
  }

  const fieldError = (name: string): string | undefined =>
    create.error instanceof ApiError ? create.error.fields[name] : undefined;

  const formError =
    create.error instanceof ApiError && Object.keys(create.error.fields).length === 0
      ? create.error.message
      : null;

  if (!projectId) {
    return (
      <>
        <p className="alert" role="alert">
          It is not clear which project this invoice is for. Start from the project page.
        </p>
        <Link to="/projects" className="btn">
          Open projects
        </Link>
      </>
    );
  }

  if (project.isPending) return <p className="muted">Loading…</p>;

  const amount = Number(amountInr);
  const gst = Number(gstPercent);
  const preview =
    Number.isFinite(amount) && amount > 0 && Number.isFinite(gst)
      ? grossInr({ amountInr: amount, gstPercent: gst })
      : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>New invoice</h1>
          <p className="muted">
            {project.data?.project.business ?? 'Project'} ·{' '}
            {project.data ? formatInr(project.data.project.priceInr) : ''} ka project
          </p>
        </div>
      </div>

      <form className="card stack" onSubmit={onSubmit} noValidate>
        <fieldset className="field" style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ padding: 0, marginBottom: 6 }}>Type</legend>
          <div className="row">
            {(['advance', 'final'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={type === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
                aria-pressed={type === value}
                onClick={() => setType(value)}
              >
                {value === 'advance' ? 'Advance' : 'Final'}
              </button>
            ))}
          </div>
          <span className="muted" style={{ fontSize: 13 }}>
            Care plan ki invoice apne aap banti hai — yahan se nahi.
          </span>
        </fieldset>

        <div className="field">
          <label htmlFor="amountInr">Amount (₹, before GST)</label>
          <input
            id="amountInr"
            type="number"
            inputMode="numeric"
            min={1}
            value={amountInr}
            onChange={(event) => setAmountInr(event.target.value)}
          />
          {fieldError('amountInr') ? (
            <span className="field__error" role="alert">
              {fieldError('amountInr')}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="gstPercent">GST %</label>
          <input
            id="gstPercent"
            type="number"
            inputMode="numeric"
            min={0}
            max={28}
            value={gstPercent}
            onChange={(event) => setGstPercent(event.target.value)}
          />
          {fieldError('gstPercent') ? (
            <span className="field__error" role="alert">
              {fieldError('gstPercent')}
            </span>
          ) : null}
        </div>

        {preview !== null ? (
          <p className="muted" style={{ margin: 0 }}>
            The client pays: <strong>{formatInr(preview)}</strong>
          </p>
        ) : null}

        {formError ? (
          <p className="alert" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="row">
          <button className="btn" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create invoice'}
          </button>
          <Link to={`/projects/${projectId}`} className="btn btn--ghost">
            Cancel
          </Link>
        </div>

        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Number apne aap milega, financial year ke hisaab se.
        </p>
      </form>
    </>
  );
}
